import React from "react";
import { Roles } from "@beable/roster-backend-client-v1";
import Login from "./components/Login/Login";
import NotFound from "./components/NotFound/NotFound";
import { Routes, Route, Navigate } from "react-router";
import Landing from "./components/Landing";
import ContentLoadingRedirect from "./components/Login/ContentLoadingRedirect";
import Standards from "./components/Standards/Standards";
import { CustomerProvider } from "./context/CustomerContext";
import BatchStatus from "./components/BatchStatus/BatchStatus";
import ClosedDates from "./components/Courses/ClosedDates/ClosedDates";
import CourseSets from "./components/Courses/CourseSets/CourseSets";
import ProvisionCourses from "./components/Courses/ProvisionCourses";
import TestDates from "./components/Courses/TestDates/TestDates";
import CustomerSetup from "./components/CustomerSetup/CustomerSetup";
import EmployeeBatch from "./components/EmployeeBatch/EmployeeBatch";
import RemoveSchool from "./components/EmployeeBatch/RemoveSchool";
import DataImport from "./components/FileUpload/DataImport";
import SummerClasses from "./components/Metadata/SummerClasses";
import ManageContests from "./components/Motivation/Contest/ManageContests";
import ManagePrizes from "./components/Motivation/Contest/ManagePrizes";
import ManageMerchandise from "./components/Motivation/Merchandise/ManageMerchandise";
import ManageQuestionHelps from "./components/Motivation/QuestionHelps/ManageQuestionHelps";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import Provision from "./components/Provision/ProvisionUsers/Provision";
import RevokeProvision from "./components/Provision/RevokeProvision.tsx/RevokeProvision";
import CourseSchedule from "./components/Schedule/Course/CourseSchedule";
import SiteManagement from "./components/SiteManagement/SiteManagement";
import SSOManagement from "./components/SSOManagement/SSOManagement";
import StaffManagement from "./components/StaffManagement/StaffManagement";
import UserManagement from "./components/UserManagement/UserManagement";
import UserSupport from "./components/UserSupport/UserSupport";
import Permissions from "./components/Permissions/Permissions";
import DataView from './components/DataView/DataView';
import Dashboard from "./components/Dashboard/Dashboard";
import Transform from "./components/Transformations/Transform";
import ManageEngagements from "./components/Motivation/Engagements/ManageEngagements";
import CreatioImports from "./components/CreatioImports/CreatioImports";
import PersonalizeEvaluator from "./components/Evaluators/PersonalizeEvaluator";
import CreatioAccountMapping from "./components/CreatioAccountMapping/CreatioAccountMapping";

export function getRouteConfig(userRole: Roles | undefined) {
    const loginRoutes = [
        {
            path: '/login',
            element: <Login />
        },
        {
            path: '/signed-out',
            element: <Navigate to='/login' />
        }
    ];

    if (userRole === Roles.contentloader) {
        return [
            ...loginRoutes,
            {
                path: '*',
                element: (<Routes>
                    <Route
                        path='/'
                        element={<Landing />}
                    />
                    <Route
                        path='/content-loading-redirect'
                        element={<ContentLoadingRedirect />}
                    />
                    <Route
                        path='/landing'
                        element={<Landing />}
                    />
                    <Route
                        path='/standards'
                        element={<Standards />}
                    />
                    <Route path='*' element={<NotFound />} />
                </Routes>),
                errorElement: <NotFound />
            }
        ];
    }

    if (userRole === Roles.standardscorrelator) {
        return [
            ...loginRoutes,
            {
                path: '*',
                element: (<Routes>
                    <Route
                        path='/'
                        element={<Standards />}
                    />
                    <Route
                        path='/standards'
                        element={<Standards />}
                    />
                    <Route path='*' element={<NotFound />} />
                </Routes>),
                errorElement: <NotFound />
            }
        ];
    }

    return [
        ...loginRoutes,
        {
            path: '*',
            element: (<CustomerProvider>
                <Routes>
                    <Route
                        path='/'
                        element={<ProtectedRoute Component={BatchStatus} />}
                    />
                    <Route
                        path='/provision-users'
                        element={<ProtectedRoute Component={Provision} />}
                    />
                    <Route
                        path='/revoke-provision'
                        element={<ProtectedRoute Component={RevokeProvision} />}
                    />
                    <Route
                        path='/provision-courses'
                        element={<ProtectedRoute Component={ProvisionCourses} />}
                    />
                    <Route
                        path='/closed-dates'
                        element={<ProtectedRoute Component={ClosedDates} />}
                    />
                    <Route
                        path='/course-schedule'
                        element={<ProtectedRoute Component={CourseSchedule} />}
                    />
                    <Route
                        path='/course-sets'
                        element={<ProtectedRoute Component={CourseSets} />}
                    />
                    <Route
                        path='/test-dates'
                        element={<ProtectedRoute Component={TestDates} />}
                    />
                    <Route
                        path='/manage-contests'
                        element={<ProtectedRoute Component={ManageContests} />}
                    />
                    <Route
                        path='/manage-prizes'
                        element={<ProtectedRoute Component={ManagePrizes} />}
                    />
                    <Route
                        path='/manage-merchandise'
                        element={<ProtectedRoute Component={ManageMerchandise} />}
                    />
                    <Route
                        path='/manage-question-helps'
                        element={<ProtectedRoute Component={ManageQuestionHelps} />}
                    />
                    <Route
                        path='/manage-engagements/:engagementId?'
                        element={<ProtectedRoute Component={ManageEngagements} />}
                    />
                    <Route
                        path='/support'
                        element={<ProtectedRoute Component={UserSupport} />}
                    />
                    <Route
                        path='/transform'
                        element={<ProtectedRoute Component={Transform} />}
                    />
                    <Route
                        path='/sites'
                        element={<ProtectedRoute Component={SiteManagement} />}
                    />
                    <Route
                        path='/upload'
                        element={<ProtectedRoute Component={DataImport} />}
                    />
                    <Route
                        path='/dashboard'
                        element={<ProtectedRoute Component={Dashboard} />}
                    />
                    <Route
                        path='/manage'
                        element={<ProtectedRoute Component={UserManagement} />}
                    />
                    <Route
                        path='/batch-status'
                        element={<ProtectedRoute Component={BatchStatus} />}
                    />
                    <Route
                        path='/employee-batch'
                        element={<ProtectedRoute Component={EmployeeBatch} />}
                    />
                    <Route
                        path='/remove-school'
                        element={<ProtectedRoute Component={RemoveSchool} />}
                    />
                    <Route
                        path='/customer-setup'
                        element={<ProtectedRoute Component={CustomerSetup} />}
                    />
                    <Route
                        path='/data-view'
                        element={<ProtectedRoute Component={DataView} />}
                    />
                    <Route
                        path='/sso'
                        element={<ProtectedRoute Component={SSOManagement} />}
                    />
                    <Route
                        path='/content-loading-redirect'
                        element={<ProtectedRoute Component={ContentLoadingRedirect} />}
                    />
                    <Route
                        path='/staff-accounts'
                        element={<ProtectedRoute Component={StaffManagement} />}
                    />
                    <Route
                        path='/customer-permissions'
                        element={<ProtectedRoute Component={Permissions} />}
                    />
                    <Route
                        path='/personalize-evaluator'
                        element={<ProtectedRoute Component={PersonalizeEvaluator} />}
                    />
                    <Route
                        path='/summer-classes'
                        element={<ProtectedRoute Component={SummerClasses} />}
                    />
                    <Route
                        path='/creatio-imports'
                        element={<ProtectedRoute Component={CreatioImports} />}
                    />
                    <Route
                        path='/account-mapping'
                        element={<ProtectedRoute Component={CreatioAccountMapping} />}
                    />
                    <Route path='*' element={<NotFound />} />
                </Routes>
            </CustomerProvider>),
            errorElement: <NotFound />
        }
    ];
}
